import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Map, BarChart3, Globe } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useStateChargemasterStats } from '@/hooks/useStateChargemasterStats';
import { useStateScenarioStats } from '@/hooks/useStateScenarioStats';
import { useStateInternationalStats } from '@/hooks/useStateInternationalStats';
import { StateChargemasterView } from '@/components/state-comparison/StateChargemasterView';
import { StateScenarioView } from '@/components/state-comparison/StateScenarioView';
import { StateInternationalView } from '@/components/state-comparison/StateInternationalView';
import { PatrioticLoader } from '@/components/state-comparison/PatrioticLoader';
import { DignityNarrationPlayer } from '@/components/dignity/DignityNarrationPlayer';

type TabKey = 'chargemaster' | 'scenarios' | 'international';

const StateComparison = () => {
  const { t } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  // URL params
  const tabParam = searchParams.get('tab') as TabKey | null;
  const stateParam = searchParams.get('state');

  const [activeTab, setActiveTab] = useState<TabKey>(
    tabParam === 'scenarios' ? 'scenarios'
    : tabParam === 'international' ? 'international'
    : 'chargemaster'
  );
  const [selectedState, setSelectedState] = useState<string | null>(stateParam?.toUpperCase() || null);

  // Sync URL params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (activeTab !== 'chargemaster') params.tab = activeTab;
    if (selectedState) params.state = selectedState;
    setSearchParams(params, { replace: true });
  }, [activeTab, selectedState, setSearchParams]);

  // Data
  const { stats: chargemasterStats, nationalStats, isLoading: chargeLoading } = useStateChargemasterStats();
  const { categoryStats, overallStats, categories: scenarioCategories, isLoading: scenarioLoading } = useStateScenarioStats();
  const {
    categoryComparison,
    overallComparison,
    countries,
    categories: intlCategories,
    isLoading: intlLoading,
  } = useStateInternationalStats();

  const isLoading = activeTab === 'chargemaster'
    ? chargeLoading
    : activeTab === 'scenarios'
    ? scenarioLoading
    : intlLoading || scenarioLoading; // international tab needs scenario stats too for state markups

  // Theme
  const pageBg = isAhp ? (isDark ? '#0a1628' : '#FAF9F6') : undefined;
  const cardBg = isAhp
    ? (isDark ? 'rgba(10,22,40,0.85)' : 'rgba(255,255,255,0.95)')
    : 'rgba(0,0,0,0.85)';
  const borderColor = isAhp
    ? (isDark ? 'rgba(197,165,90,0.2)' : 'rgba(0,0,0,0.1)')
    : 'rgba(255,255,255,0.1)';
  const textColor = isAhp ? (isDark ? '#FAF9F6' : '#1B1B1B') : '#fff';
  const mutedText = isAhp ? (isDark ? 'rgba(250,249,246,0.6)' : 'rgba(0,0,0,0.5)') : 'rgba(255,255,255,0.5)';
  const accentColor = isAhp ? '#C5A55A' : '#00ff41';

  const tabs: { key: TabKey; label: string; shortLabel: string; icon: typeof Map }[] = [
    { key: 'chargemaster', label: t('stateComparison.tabs.markup'), shortLabel: t('stateComparison.tabs.markupShort'), icon: Map },
    { key: 'scenarios', label: t('stateComparison.tabs.scenarios'), shortLabel: t('stateComparison.tabs.scenariosShort'), icon: BarChart3 },
    { key: 'international', label: t('stateComparison.tabs.international'), shortLabel: t('stateComparison.tabs.internationalShort'), icon: Globe },
  ];

  return (
    <>
      <Helmet>
        <title>{isAhp ? t('stateComparison.meta.titleAhp', { brandName: brand.name }) : t('stateComparison.meta.titleLuigi', { brandName: brand.name })}</title>
        <meta name="description" content={t('stateComparison.meta.description')} />
      </Helmet>

      <div
        className="min-h-screen pb-20"
        style={{
          backgroundColor: pageBg,
          paddingTop: '16px',
        }}
      >
        <div className="max-w-7xl mx-auto px-4">
          {/* Page header */}
          <div className="mb-6">
            <h1
              className="text-2xl md:text-3xl font-bold"
              style={{
                color: textColor,
                fontFamily: isAhp ? "'Instrument Serif', Georgia, serif" : undefined,
              }}
            >
              {isAhp ? t('stateComparison.headingAhp') : t('stateComparison.headingLuigi')}
            </h1>
            <p className="text-sm mt-1" style={{ color: mutedText }}>
              {t('stateComparison.subtitle')}
            </p>
          </div>

          {/* Dignity narration — guided tour of the page */}
          <div style={{ marginBottom: 16 }}>
            <DignityNarrationPlayer pageKey="state-comparison" />
          </div>

          {/* Tab bar */}
          <div
            className="flex gap-1 p-1 rounded-xl mb-6"
            style={{ backgroundColor: isAhp ? (isDark ? 'rgba(0,20,60,0.5)' : 'rgba(0,0,0,0.05)') : 'rgba(255,255,255,0.06)' }}
          >
            {tabs.map((tab) => {
              const active = activeTab === tab.key;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
                  style={{
                    backgroundColor: active ? cardBg : 'transparent',
                    color: active ? textColor : mutedText,
                    border: active ? `1px solid ${borderColor}` : '1px solid transparent',
                    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                  }}
                >
                  <Icon size={16} style={{ color: active ? accentColor : mutedText }} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                </button>
              );
            })}
          </div>

          {/* Loading state */}
          {isLoading && <PatrioticLoader />}

          {/* Tab content */}
          {!isLoading && activeTab === 'chargemaster' && (
            <StateChargemasterView
              stats={chargemasterStats}
              nationalStats={nationalStats}
              selectedState={selectedState}
              onStateSelect={setSelectedState}
            />
          )}

          {!isLoading && activeTab === 'scenarios' && (
            <StateScenarioView
              categoryStats={categoryStats}
              overallStats={overallStats}
              categories={scenarioCategories}
              selectedState={selectedState}
              onStateSelect={setSelectedState}
            />
          )}

          {!isLoading && activeTab === 'international' && (
            <StateInternationalView
              categoryComparison={categoryComparison}
              overallComparison={overallComparison}
              countries={countries}
              categories={intlCategories}
              stateOverallStats={overallStats}
              selectedState={selectedState}
              onStateSelect={setSelectedState}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default StateComparison;
